import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useViewConfig } from "../hooks/useViewConfig";
import "../styles/promotionalPopup.css";

const SESSION_KEY = "regardtraders:promo-popup-dismissed";

const PromotionalPopup = () => {
  const navigate = useNavigate();
  const { data: config, loading } = useViewConfig("PROMOTIONAL_POPUP");
  const [isOpen, setIsOpen] = useState(false);
  const [isRendered, setIsRendered] = useState(false);

  const popupData = config?.data;

  useEffect(() => {
    if (loading || !popupData || !popupData.enabled) {
      setIsOpen(false);
      setIsRendered(false);
      return;
    }

    const dismissed = sessionStorage.getItem(SESSION_KEY);
    if (dismissed) {
      return;
    }

    // Require an image URL to render. If empty, render nothing.
    if (!popupData.image || !popupData.image.url || !popupData.image.url.trim()) {
      return;
    }

    setIsRendered(true);

    const delayTime = typeof popupData.delay === "number" ? popupData.delay : 500;
    const timer = setTimeout(() => {
      setIsOpen(true);
    }, delayTime);

    return () => clearTimeout(timer);
  }, [popupData, loading]);

  if (loading || !isRendered || !popupData || !popupData.enabled) {
    return null;
  }

  // Session storage check to prevent displaying if dismissed
  if (sessionStorage.getItem(SESSION_KEY)) {
    return null;
  }

  const handleClose = () => {
    setIsOpen(false);
    sessionStorage.setItem(SESSION_KEY, "true");
  };

  const handleCtaClick = () => {
    const link = popupData.button?.link;
    if (!link) return;

    handleClose();

    if (/^https?:\/\//.test(link)) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(link);
    }
  };

  const hasCta = popupData.button && popupData.button.enabled && popupData.button.text && popupData.button.text.trim() !== "";
  
  // Set up responsive visibility classes
  let responsiveClass = "";
  if (popupData.showOnMobile === false) {
    responsiveClass += " promo-popup--hide-mobile";
  }
  if (popupData.showOnDesktop === false) {
    responsiveClass += " promo-popup--hide-desktop";
  }

  return (
    <div className={`promo-popup-overlay ${isOpen ? "open" : ""} ${responsiveClass}`}>
      <div className="promo-popup-content-wrapper">
        
        {/* Close Button is a sibling of the modal so it stays outside the modal clipping limits */}
        <button 
          className="promo-popup-close-btn" 
          onClick={handleClose}
          aria-label="Close Popup"
        >
          &times;
        </button>

        <div className="promo-popup-modal">
          
          {/* Clickable Banner Image (Clicking anywhere on the creative navigates) */}
          <div 
            className="promo-popup-image-container clickable" 
            onClick={handleCtaClick}
            title={popupData.button?.enabled ? `Go to ${popupData.button.link}` : ""}
            style={{ cursor: popupData.button?.link ? "pointer" : "default" }}
          >
            <img 
              src={popupData.image.url} 
              alt={popupData.image.alt || "Promotional Banner"} 
              className="promo-popup-image"
            />

            {/* Floating modern pill CTA button */}
            {hasCta && (
              <button 
                className="promo-popup-cta-pill" 
                onClick={(e) => {
                  e.stopPropagation(); // Avoid triggering double click handlers
                  handleCtaClick();
                }}
              >
                {popupData.button.text}
              </button>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default PromotionalPopup;
